import { Component, OnInit, OnDestroy } from '@angular/core';
import { WindowReferenceService, ICustomWindow, LoadingService } from '@core/shared';
import { AccountsService } from '../services/accounts.service';
import { AuthenticationService } from '@core/startup';
import { Subscription } from 'rxjs';
import { Router, RouterEvent } from '@angular/router';
import { filter, startWith } from 'rxjs/operators';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { browserRefresh } from '@app/app.component';
import { HttpImageService } from '@core/shared/services/http-img.service';
import { PanVerificationScope } from '@app/core/shared/constants/pan-verification-scope';
import { SelfRegistrationFields } from '@app/core/shared/constants/self-registration-fields';


let windowObject = window as any;

@Component({
  selector: 'app-edit-profile-selfregistration',
  templateUrl: './edit-profile-selfregistration.component.html'
})
export class EditProfileSelfRegistrationComponent implements OnInit, OnDestroy {

  private _window: ICustomWindow;
  private _debugMessageListener!: (event: MessageEvent) => void;
  private _panRevalidationPending = false;
  private _panForRevalidation = '';
  private _panStatusNeedsRestoreToFailed = false;
  public userId!: string;
  selectedCulture!: string;
  contentPageId!: string;
  contentPageDetails: any;
  languageChangeSubscription!: Subscription;
  navigationStartSubscription!: Subscription;
  browserRefresh!: boolean;

  constructor(public accountsService: AccountsService, public windowRef: WindowReferenceService, public authService: AuthenticationService,
    private translateService: TranslateService, private router: Router, public loadingService: LoadingService,
    private imgService: HttpImageService) {
    this._window = windowRef.nativeWindow;
  }
  ngOnInit(): void {
    // Intercept postMessage events from the SDK iframe for debugging
    this._debugMessageListener = (event: MessageEvent) => {
      console.log('[EditProfile] postMessage received from origin:', event.origin,
        '| data:', JSON.stringify(event.data));
    };
    window.addEventListener('message', this._debugMessageListener);

    windowObject.parent.accountsService = this.accountsService;
    windowObject.parent.translate = this.translateService;

    windowObject.parent.preRegistrationCallBack = async (userDetails: any): Promise<any> => {
      // Reset revalidation state at the start of every new preview attempt.
      // Prevents stale _panRevalidationPending = true from a prior OK interaction
      // (e.g. if postRegistrationCallBack was never called) from affecting a later Cancel.
      this._panRevalidationPending = false;
      this._panForRevalidation = '';
      this._panStatusNeedsRestoreToFailed = false;
      console.log('[PanRevalidation] preRegistrationCallBack triggered.');

      let pan: string = '';
      let firstName: string = '';
      let lastName: string = '';

      let fields = Object.values(userDetails);
      fields.forEach(parseUserDetails);

      function parseUserDetails(item: any) {
        if (item.fieldName) {
          if (item.fieldName.toLowerCase() == SelfRegistrationFields.Pan.toLowerCase()) {
            pan = item.fieldValue;
          }
          if (item.fieldName.toLowerCase() == SelfRegistrationFields.FirstName.toLowerCase()) {
            firstName = item.fieldValue;
          }
          if (item.fieldName.toLowerCase() == SelfRegistrationFields.LastName.toLowerCase()) {
            lastName = item.fieldValue;
          }
        }
      }


      // ── Step 1: Check if an active revalidation config exists (IsDeleted=0) ─
      let revalidationStatus: any = null;
      try {
        revalidationStatus = await windowObject.parent.accountsService
          .getMyRevalidationStatus()
          .toPromise();
        console.log('[PanRevalidation] status:', JSON.stringify(revalidationStatus));
      } catch (err) {
        console.error('[PanRevalidation] getMyRevalidationStatus error — falling back to existing flow:', err);
      }

      // ── Step 2: Active config found → new PAN revalidation flow ─────────
      if (revalidationStatus && revalidationStatus.isConfigActive) {
        const panStatus: string = (revalidationStatus.panStatus || '').toLowerCase();
        const attemptsLeft: number = revalidationStatus.attemptsLeft || 0;
        console.log('[PanRevalidation] Active config. panStatus:', panStatus, '| attemptsLeft:', attemptsLeft);

        // ── Scenario A: PAN status is Failed ─────────────────────────────
          if (panStatus === 'failed' || panStatus === '' || panStatus === null) {
          // Date helper shared by all sub-scenarios in the Failed path
          const fmtDate = (dt: any): string => {
            if (!dt) { return 'N/A'; }
            const d = new Date(dt);
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${dd}/${mm}/${d.getFullYear()}`;
          };
          const startDateStr = fmtDate(revalidationStatus.configStartDate);
          const endDateStr   = fmtDate(revalidationStatus.configEndDate);

          if (attemptsLeft <= 0) {
            // Max limit reached — check if user changed PAN (blank also counts as a change)
            let panChangedOnLimit = false;
            try {
              const panCheck = await windowObject.parent.accountsService
                .isPanModifyAllowed()
                .toPromise();
              if (panCheck && panCheck.pan) {
                panChangedOnLimit = (pan || '').toUpperCase() !== (panCheck.pan || '').toUpperCase();
              }
            } catch (err) {
              console.warn('[PanRevalidation] isPanModifyAllowed check failed — treating PAN as unchanged:', err);
            }

            if (panChangedOnLimit) {
              // PAN changed + limit reached: block submit, SDK shows popup.
              return Promise.resolve({
                success: false,
                errorCode: '',
                responseMessage: `PAN validation limit exceeded ${revalidationStatus.currentAttempt}/${revalidationStatus.maxAttemptLimit}. PAN changes are not allowed. Re-enter the existing PAN number to continue or contact your administrator`
              });
            }
            // PAN not changed + limit reached: alert (OK-only) then proceed to Submit.
            await this._customAlert(`PAN validation limit exceeded ${revalidationStatus.currentAttempt}/${revalidationStatus.maxAttemptLimit}. Please contact your administrator for support.`);
            return Promise.resolve({ success: true, errorCode: '', responseMessage: '' });
          }

          // Blank PAN — always show alert, then proceed to submit.
          // _panStatusNeedsRestoreToFailed: if user had a stored PAN and cleared it,
          // the backend sets status to Pending on save; restore call is a no-op if
          // stored PAN was also blank.
          if (!pan || pan.trim() === '') {
            await this._customAlert(`PAN number is not available. Validation will be skipped.`);
            this._panStatusNeedsRestoreToFailed = true;
            return Promise.resolve({ success: true, errorCode: '', responseMessage: '' });
          }

          // Non-blank PAN (changed or unchanged) — always show confirm dialog.
          // OK: record attempt, status → Pending.
          // Cancel: no attempt, status stays Failed.
          const confirmed = await this._customConfirm(
            `Proceed with PAN validation? You have ${attemptsLeft}/${revalidationStatus.maxAttemptLimit} attempt(s) remaining.`
          );
          console.log('[PanRevalidation] User confirmed revalidation:', confirmed);

          if (confirmed) {
            // Flag intent; actual API call happens in postRegistrationCallBack after Submit
            this._panRevalidationPending = true;
            this._panForRevalidation = pan;
          } else {
            // User declined — backend may set Pending on PAN change; restore to Failed.
            this._panStatusNeedsRestoreToFailed = true;
          }
          // Both OK and Cancel proceed to Submit.
          return Promise.resolve({ success: true, errorCode: '', responseMessage: '' });
        }

        // ── Scenario B: PAN status is Success or Pending ──────────────────
        // PAN changes (including clearing to blank) are not permitted.
        // Check whether user changed the PAN number
        let panChanged = false;
        try {
          const panCheck = await windowObject.parent.accountsService
            .isPanModifyAllowed()
            .toPromise();
          if (panCheck && panCheck.pan) {
            panChanged = (pan || '').toUpperCase() !== (panCheck.pan || '').toUpperCase();
          }
        } catch (err) {
          console.warn('[PanRevalidation] isPanModifyAllowed check failed — treating PAN as unchanged:', err);
        }

        if (panChanged) {
          // Block form submission — PAN must not be changed when status is Pending or Success. SDK shows popup.
          const message = panStatus === 'pending'
            ? 'PAN verification is in progress. PAN changes are not allowed. Re-enter the PAN number to continue submitting other details.'
            : 'PAN already verified successfully. PAN changes are not allowed. Re-enter the existing PAN number to continue.';
          return Promise.resolve({ success: false, errorCode: '', responseMessage: message });
        }
        // PAN not changed → proceed to Submit
        return Promise.resolve({ success: true, errorCode: '', responseMessage: '' });
      }

      // ── Step 3: No active config → existing isPanModifyAllowed flow ──────
      console.log('[PanRevalidation] No active config — using existing isPanModifyAllowed flow.');

      // Condition 6: blank PAN — proceed to Submit
      if (!pan || pan.trim() === '') {
        return Promise.resolve({ success: true, errorCode: '', responseMessage: '' });
      }

      let panPromise = await windowObject.parent.accountsService
        .isPanModifyAllowed()
        .toPromise();

      return Promise.resolve(this.checkPan(panPromise, pan, firstName, lastName));
    };

    windowObject.parent.postRegistrationCallBack = async (userDetails: any, message: any) => {
      // The SDK calls this callback from inside a React render function, so it can fire
      // multiple times (once per re-render) while the success state is active.
      // Guard against duplicate execution.
      if ((windowObject.parent as any)._postCallbackHandled) { return; }
      (windowObject.parent as any)._postCallbackHandled = true;
      if (this._panRevalidationPending) {
        this._panRevalidationPending = false;
        const panNumber = this._panForRevalidation;
        this._panForRevalidation = '';
        try {
          await windowObject.parent.accountsService.recordRevalidationAttempt(undefined, panNumber).toPromise();
          console.log('[PanRevalidation] Attempt recorded after Submit.');
        } catch (err) {
          console.error('[PanRevalidation] recordRevalidationAttempt error after Submit:', err);
        }
      } else if (this._panStatusNeedsRestoreToFailed) {
        this._panStatusNeedsRestoreToFailed = false;
        try {
          await windowObject.parent.accountsService.restoreFailedStatus().toPromise();
          console.log('[PanRevalidation] PAN status restored to Failed after Cancel.');
        } catch (err) {
          console.error('[PanRevalidation] restoreFailedStatus error after Submit:', err);
        }
      }
      location.reload();
    };

    windowObject.parent.addressCallback = async (val: any, data: any) => {

      let details = {
        "localeCode": this.selectedCulture,
        "code": data
      };
      switch (val) {
        case "COUNTRY":
          let countryPromise = await this.accountsService.getCountries(details);
          return countryPromise.country;
        case "STATE":
          let statesPromise = await this.accountsService.getStates(details);
          return statesPromise.state;
        case "CITY":
          let cityPromise = await this.accountsService.getCities(details);
          return cityPromise.city;
        default:
          return Promise.reject();
      }
    }

    this.navigationStartSubscription = this.router.events.pipe(
      filter((event) => event instanceof RouterEvent),
      startWith('Initial call')
    ).subscribe((res) => {
      this.browserRefresh = browserRefresh;
      this.getUser();
    });

    this.languageChangeSubscription = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      if (!this.browserRefresh) {
        window.location.reload();
        this.getContentPage(this.contentPageId);
      }
      else
        this.browserRefresh = false;
    });
  }

  checkPan(panPromise: any, pan: string, firstName: string, lastName: string) {
    let success = {
      success: true,
      errorCode: "",
      responseMessage: "",
    };
    if (panPromise && !panPromise.isPanModifyAllowed) {
      if (pan != panPromise.pan) {
        success.success = false;
        success.responseMessage = windowObject.parent.translate.instant(
          "Accounts.PanNotAllowedToModify"
        );
      } else if (
        (panPromise.firstName + panPromise.lastName).toLowerCase() !=
        (firstName + lastName).toLowerCase() &&
        panPromise.panVerificationScope ==
        PanVerificationScope.PanWithOwnersName
      ) {
        success.success = false;
        success.responseMessage = windowObject.parent.translate.instant(
          "Accounts.NameNotAllowedToModify"
        );
      }
    }
    return success;
  }

  getContentPage(contentPageId: string) {
    this.selectedCulture = localStorage.getItem('culture') || '';
    this.loadingService.doLoading(this.accountsService.getContentPage(contentPageId, this.selectedCulture), this)
      .subscribe(response => {
        this.contentPageDetails = this.imgService.parseHtml(response);
        setTimeout(() => {
          this.fetchBlobImages();
        });
      });
  }

  fetchBlobImages() {
    let images = document.querySelectorAll('img[data-src]');
    for (let image of Array.from(images)) {
      let dataSrc = image.getAttribute('data-src');
      this.imgService.getImage(dataSrc).subscribe(val => {
        if (!val) { return; }
        let type = val.type;
        let reader = new FileReader();
        reader.readAsDataURL(val);
        reader.onloadend = function () {
          let baseImage = <string>reader.result;
          image.setAttribute('src', `data:${type};base64,` + baseImage.split(',')[1]);
        }
      });
    }
  }

  getUser() {
    this.selectedCulture = localStorage.getItem('culture') || '';
    this.authService.getCurrentUser().subscribe(response => {
      this.userId = response.id;
      this.accountsService.selfRegistrationConfig().subscribe(data => {
        this.renderForm(data);
        this.contentPageId = data.contentUserEditFormId;
        this.getContentPage(this.contentPageId);
      })
    });
  }

  renderForm(configs: any) {
    this.accountsService.editProfileSelfRegistration(configs, this.userId, this.selectedCulture).subscribe(data => {
      // Patch XHR BEFORE loading SDK so we can inject enablePreviewValidation=true
      // into the SDK's own internal API response (registration-form/.../attributes/edit).
      // Setting it on window.__selfRegistrationAppData__ does NOT work — the SDK ignores
      // that field and overwrites formDetails from its own GET_DATA_FULFILLED API call.
      this._patchXhrForSdkFormResponse();

      this._window.__selfRegistrationAppData__ = data;
      this._window.loadSelfRegistrationInDom('selfRegistrationRoot');
    });
  }

  /**
   * Intercepts the XMLHttpRequest used by the SDK's bundled axios to inject
   * `enablePreviewValidation: true` into the form config response.
   * Without this flag the SDK's onPreview handler silently returns {success:true}
   * and never calls window.parent.preRegistrationCallBack.
   */
  private _patchXhrForSdkFormResponse(): void {
    const originalOpen = XMLHttpRequest.prototype.open;

    (XMLHttpRequest.prototype as any).open = function(method: string, url: string, ...rest: any[]) {
      if (url && url.includes('registration-form') && url.includes('/attributes')) {
        // Register our readystatechange listener FIRST (before axios sets onreadystatechange)
        // so it fires before axios reads responseText.
        this.addEventListener('readystatechange', function(this: XMLHttpRequest) {
          if (this.readyState === 4 && this.status === 200) {
            try {
              const data = JSON.parse(this.responseText);
              if (data && data.form) {
                data.form.enablePreviewValidation = true;
                console.log('[XHR Patch] Injected enablePreviewValidation=true into SDK form response ✓');
              }
              const modified = JSON.stringify(data);
              Object.defineProperty(this, 'responseText', {
                get: () => modified,
                configurable: true
              });
            } catch (e) {
              console.error('[XHR Patch] Failed to patch SDK form response:', e);
            }
          }
        });

        // Restore prototype immediately — only the FIRST matching XHR needs patching.
        XMLHttpRequest.prototype.open = originalOpen;
      }
      return originalOpen.apply(this, arguments as any);
    };
  }



  private _customAlert(message: string): Promise<void> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';
      const box = document.createElement('div');
      box.style.cssText = 'background:#fff;padding:24px;border-radius:8px;max-width:420px;width:90%;text-align:center;box-shadow:0 4px 12px rgba(0,0,0,0.3);font-family:inherit;';
      const msg = document.createElement('p');
      msg.style.cssText = 'margin:0 0 20px;font-size:14px;color:#333;line-height:1.6;white-space:pre-wrap;';
      msg.textContent = message;
      const btn = document.createElement('button');
      btn.textContent = 'OK';
      btn.style.cssText = 'padding:8px 28px;background:#007bff;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:14px;';
      btn.onclick = () => { document.body.removeChild(overlay); resolve(); };
      box.appendChild(msg);
      box.appendChild(btn);
      overlay.appendChild(box);
      document.body.appendChild(overlay);
      btn.focus();
    });
  }

  private _customConfirm(message: string): Promise<boolean> {
    return new Promise((resolve) => {
      const overlay = document.createElement('div');
      overlay.style.cssText = 'position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:99999;display:flex;align-items:center;justify-content:center;';
      const box = document.createElement('div');
      box.style.cssText = 'background:#fff;padding:24px;border-radius:8px;max-width:420px;width:90%;text-align:center;box-shadow:0 4px 12px rgba(0,0,0,0.3);font-family:inherit;';
      const msg = document.createElement('p');
      msg.style.cssText = 'margin:0 0 20px;font-size:14px;color:#333;line-height:1.6;white-space:pre-wrap;';
      msg.textContent = message;
      const btnRow = document.createElement('div');
      btnRow.style.cssText = 'display:flex;justify-content:center;gap:12px;';
      const btnOk = document.createElement('button');
      btnOk.textContent = 'OK';
      btnOk.style.cssText = 'padding:8px 28px;background:#007bff;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:14px;';
      const btnCancel = document.createElement('button');
      btnCancel.textContent = 'Cancel';
      btnCancel.style.cssText = 'padding:8px 28px;background:#6c757d;color:#fff;border:none;border-radius:4px;cursor:pointer;font-size:14px;';
      btnOk.onclick = () => { document.body.removeChild(overlay); resolve(true); };
      btnCancel.onclick = () => { document.body.removeChild(overlay); resolve(false); };
      btnRow.appendChild(btnOk);
      btnRow.appendChild(btnCancel);
      box.appendChild(msg);
      box.appendChild(btnRow);
      overlay.appendChild(box);
      document.body.appendChild(overlay);
      btnOk.focus();
    });
  }

  ngOnDestroy() {
    if (this.languageChangeSubscription)
      this.languageChangeSubscription.unsubscribe();
    if (this.navigationStartSubscription)
      this.navigationStartSubscription.unsubscribe();
    if (this._debugMessageListener)
      window.removeEventListener('message', this._debugMessageListener);
  }

  isShownDivMaterial: boolean = false; // hidden by default
  toggleShowsMaterial() {
    this.isShownDivMaterial = !this.isShownDivMaterial;
  }
}
